import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { z } from 'zod';
import { registerLib, createScopedErrors, registerCodes, reportInvalidInput } from '@norkron/web-errors';

// src/index.ts
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-color";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const instances = Array.from(
      root.querySelectorAll('[data-norkron-lib^="@norkron/web-color"]')
    );
    if (instances.length === 0) return findings;
    for (const instance of instances) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "WC_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      if (instance.getAttribute("data-color-gamut") === "out") {
        findings.push({
          libId: LIB_ID,
          code: "WC_OUT_OF_GAMUT",
          category: "layout",
          severity: "minor",
          message: "Color value lies outside the displayable gamut for the current display capability.",
          selector: selectorFor(instance),
          suggestedFix: "Clip/clamp the colour into the target gamut before display."
        });
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "WC_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}
var COLOR_LIB = "@norkron/web-color";
var COLOR_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.color");
try {
  registerLib(COLOR_LIB, COLOR_TOKEN);
} catch {
}
var { warn, panic } = createScopedErrors(COLOR_LIB, COLOR_TOKEN);
try {
  registerCodes(COLOR_LIB, {
    COLOR_INVALID_INPUT: {
      description: "A color parsing or conversion entry point received an input it cannot interpret as a valid color (malformed hex string, out-of-range RGB channel, non-finite OKLCH/HSL component). The function then throws `Error` as before; this warning surfaces the same condition through the structured telemetry sink so consumers see it via the central error pipeline rather than only when a try/catch is in place.",
      severity: "warning",
      docsAnchor: "color/invalid-input",
      exampleFix: "Validate the input upstream (typeof string, length 3/6, /^[0-9a-fA-F]+$/), or wrap the call in try/catch and recover with a documented fallback color."
    },
    WEB_COLOR_INVALID_INPUT: {
      description: "A public input to @norkron/web-color failed zod validation (Wave 10 schema mandate). Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib.",
      severity: "warning",
      docsAnchor: "https://norkron.dev/web-errors/WEB_INVALID_INPUT_SHAPE"
    },
    WC_OUT_OF_GAMUT: {
      description: 'A color advertised via `data-color-gamut="out"` lies outside the displayable gamut for the current display capability. Emitted as a selfCheck finding so consumers can clamp into the target gamut before display.',
      severity: "warning",
      docsAnchor: "web-color/out-of-gamut",
      exampleFix: "Clip/clamp the colour into the target gamut before display (e.g. project to sRGB / display-P3 boundary along the OKLCH chroma axis)."
    },
    WC_SELFCHECK_BUDGET_EXCEEDED: {
      description: 'The web-color selfCheck walker exceeded its time budget while traversing [data-norkron-lib^="@norkron/web-color"] instances. Partial results are returned and the walker exits the loop early to preserve the bounded contract.',
      severity: "info",
      docsAnchor: "web-color/selfcheck-budget-exceeded",
      exampleFix: "Raise the budgetMs option, or reduce the number of mounted web-color instances under the audited root."
    },
    WC_SELFCHECK_INTERNAL_ERROR: {
      description: 'The web-color selfCheck walker threw while traversing [data-norkron-lib^="@norkron/web-color"] instances. Emitted as a finding (info severity) rather than re-thrown to preserve the pure/bounded/no-throw selfCheck contract.',
      severity: "info",
      docsAnchor: "web-color/selfcheck-internal-error",
      exampleFix: "Inspect the finding.message for the underlying Error message; usually indicates a host environment without working querySelectorAll. File a bug at @norkron/web-color if the root.querySelectorAll signature is standard."
    }
  });
} catch {
}

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}
var HEX_RE = /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3}([0-9a-fA-F]{2})?)?$/;
var RgbSchema = z.object({
  r: z.number().int().min(0).max(255),
  g: z.number().int().min(0).max(255),
  b: z.number().int().min(0).max(255)
});
var HexStringSchema = z.string().regex(HEX_RE, "expected a hex color");
var ColorInputSchema = z.union([HexStringSchema, RgbSchema]);
z.tuple([
  z.number().min(0).max(1),
  z.number().min(0),
  z.number().min(0).max(360)
]);
var HslSchema = z.tuple([
  z.number().min(0).max(360),
  z.number().min(0).max(100),
  z.number().min(0).max(100)
]);
var HexToRgbInputSchema = HexStringSchema;
var HexToRgbStringInputSchema = HexStringSchema;
var SrgbLuminanceInputSchema = ColorInputSchema;
var ContrastRatioInputSchema = z.object({
  fg: ColorInputSchema,
  bg: ColorInputSchema
});
var WcagGradeInputSchema = z.number().min(1);
var ApcaContrastInputSchema = z.object({
  text: ColorInputSchema,
  bg: ColorInputSchema
});
var HexToOklchInputSchema = ColorInputSchema;
var HexToHueInputSchema = HexStringSchema;
var HexToHslInputSchema = HexStringSchema;
var HslToHexInputSchema = HslSchema;

// src/validate-input.ts
function validateInput(schema, fn, input) {
  const parsed = schema.safeParse(input);
  if (!parsed.success) {
    reportInvalidInput({
      lib: COLOR_LIB,
      libToken: COLOR_TOKEN,
      fn,
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}

// src/color.ts
function warnInvalidColor(fn, prop, received, expected) {
  try {
    warn({
      fn,
      code: "COLOR_INVALID_INPUT",
      message: `${fn}: invalid color input \u2014 see docs/color/invalid-input.`,
      prop,
      received,
      expected,
      dedupKey: `${fn}:${String(received)}`
    });
  } catch {
  }
}
function normalizeHex(hex) {
  const stripped = hex.startsWith("#") ? hex.slice(1) : hex;
  if (!/^[0-9a-fA-F]+$/.test(stripped)) {
    warnInvalidColor("normalizeHex", "hex", hex, "a hex string matching /^#?[0-9a-fA-F]+$/");
    throw new Error(`Invalid hex color: "${hex}" contains non-hex characters`);
  }
  if (stripped.length === 3) {
    return (stripped[0] + stripped[0] + stripped[1] + stripped[1] + stripped[2] + stripped[2]).toLowerCase();
  }
  if (stripped.length === 6) {
    return stripped.toLowerCase();
  }
  warnInvalidColor("normalizeHex", "hex", hex, "3 or 6 hex digits (with optional # prefix)");
  throw new Error(`Invalid hex color: "${hex}" must be 3 or 6 hex digits (with optional # prefix)`);
}
function validateRgb(rgb) {
  for (const [name, val] of Object.entries(rgb)) {
    if (!Number.isInteger(val) || val < 0 || val > 255) {
      warnInvalidColor("validateRgb", name, val, "integer 0-255");
      throw new Error(
        `Invalid RGB: "${name}" must be an integer 0-255, got ${val}`
      );
    }
  }
}
function resolveRgb(input) {
  if (typeof input === "string") {
    return hexToRgb(input);
  }
  validateRgb(input);
  return [input.r, input.g, input.b];
}
function linearize(c) {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}
function hexToRgb(hex) {
  validateInput(HexToRgbInputSchema, "hexToRgb", hex);
  const h = normalizeHex(hex);
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function srgbLuminance(input) {
  validateInput(SrgbLuminanceInputSchema, "srgbLuminance", input);
  const [r, g, b] = resolveRgb(input);
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}
function contrastRatio(a, b) {
  validateInput(ContrastRatioInputSchema, "contrastRatio", { fg: a, bg: b });
  const l1 = srgbLuminance(a);
  const l2 = srgbLuminance(b);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}
function wcagGrade(ratio, options) {
  validateInput(WcagGradeInputSchema, "wcagGrade", ratio);
  if (options?.largeText) {
    if (ratio >= 4.5) return "AAA";
    if (ratio >= 3) return "AA";
    return "FAIL";
  }
  if (ratio >= 7) return "AAA";
  if (ratio >= 4.5) return "AA";
  if (ratio >= 3) return "AA-lg";
  return "FAIL";
}
var APCA_MAIN_TRC = 2.4;
var APCA_R_CO = 0.2126729;
var APCA_G_CO = 0.7151522;
var APCA_B_CO = 0.072175;
var APCA_NORM_BG = 0.56;
var APCA_NORM_TXT = 0.57;
var APCA_REV_TXT = 0.62;
var APCA_REV_BG = 0.65;
var APCA_SCALE_BOW = 1.14;
var APCA_SCALE_WOB = 1.14;
var APCA_BLK_THRS = 0.022;
var APCA_BLK_CLMP = 1.414;
var APCA_LO_CLIP = 0.1;
var APCA_LO_OFFSET = 0.027;
var APCA_DELTA_Y_MIN = 5e-4;
function apcaLinearize(c) {
  return (c / 255) ** APCA_MAIN_TRC;
}
function apcaLuminance(r, g, b) {
  return APCA_R_CO * apcaLinearize(r) + APCA_G_CO * apcaLinearize(g) + APCA_B_CO * apcaLinearize(b);
}
function apcaSoftClamp(y) {
  return y >= APCA_BLK_THRS ? y : y + (APCA_BLK_THRS - y) ** APCA_BLK_CLMP;
}
function apcaContrast(text, background) {
  validateInput(ApcaContrastInputSchema, "apcaContrast", { text, bg: background });
  const [tr, tg, tb] = resolveRgb(text);
  const [br, bg, bb] = resolveRgb(background);
  const txtYraw = apcaLuminance(tr, tg, tb);
  const bgYraw = apcaLuminance(br, bg, bb);
  const txtY = apcaSoftClamp(txtYraw);
  const bgY = apcaSoftClamp(bgYraw);
  if (Math.abs(bgY - txtY) < APCA_DELTA_Y_MIN) return 0;
  let sapc;
  if (bgY > txtY) {
    sapc = (bgY ** APCA_NORM_BG - txtY ** APCA_NORM_TXT) * APCA_SCALE_BOW;
  } else {
    sapc = (bgY ** APCA_REV_BG - txtY ** APCA_REV_TXT) * APCA_SCALE_WOB;
  }
  if (Math.abs(sapc) < APCA_LO_CLIP) return 0;
  if (sapc > 0) {
    return (sapc - APCA_LO_OFFSET) * 100;
  }
  return (sapc + APCA_LO_OFFSET) * 100;
}
function hexToOklch(input) {
  validateInput(HexToOklchInputSchema, "hexToOklch", input);
  const [r, g, b] = resolveRgb(input);
  const lr = linearize(r);
  const lg = linearize(g);
  const lb = linearize(b);
  const l_ = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m_ = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s_ = Math.cbrt(0.0883024619 * lr + 0.2818474174 * lg + 0.6302613616 * lb);
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const bOk = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  const C = Math.sqrt(a * a + bOk * bOk);
  let H = Math.atan2(bOk, a) * 180 / Math.PI;
  if (H < 0) H += 360;
  if (C < 1e-8) {
    return [L, 0, 0];
  }
  return [L, C, H];
}
function parseHexFull(hex) {
  const stripped = hex.startsWith("#") ? hex.slice(1) : hex;
  if (!/^[0-9a-fA-F]+$/.test(stripped)) {
    warnInvalidColor("parseHexFull", "hex", hex, "a hex string matching /^#?[0-9a-fA-F]+$/");
    throw new Error(`Invalid hex color: "${hex}" contains non-hex characters`);
  }
  let r;
  let g;
  let b;
  let a;
  if (stripped.length === 3) {
    r = parseInt(stripped[0] + stripped[0], 16);
    g = parseInt(stripped[1] + stripped[1], 16);
    b = parseInt(stripped[2] + stripped[2], 16);
  } else if (stripped.length === 4) {
    r = parseInt(stripped[0] + stripped[0], 16);
    g = parseInt(stripped[1] + stripped[1], 16);
    b = parseInt(stripped[2] + stripped[2], 16);
    a = parseInt(stripped[3] + stripped[3], 16);
  } else if (stripped.length === 6) {
    r = parseInt(stripped.slice(0, 2), 16);
    g = parseInt(stripped.slice(2, 4), 16);
    b = parseInt(stripped.slice(4, 6), 16);
  } else if (stripped.length === 8) {
    r = parseInt(stripped.slice(0, 2), 16);
    g = parseInt(stripped.slice(2, 4), 16);
    b = parseInt(stripped.slice(4, 6), 16);
    a = parseInt(stripped.slice(6, 8), 16);
  } else {
    warnInvalidColor("parseHexFull", "hex", hex, "3, 4, 6, or 8 hex digits (with optional # prefix)");
    throw new Error(
      `Invalid hex color: "${hex}" must be 3, 4, 6, or 8 hex digits (with optional # prefix)`
    );
  }
  return [r, g, b, a];
}
function hexToRgbString(hex) {
  validateInput(HexToRgbStringInputSchema, "hexToRgbString", hex);
  const [r, g, b, a] = parseHexFull(hex);
  if (a === void 0) {
    return `rgb(${r}, ${g}, ${b})`;
  }
  const alpha = Math.round(a / 255 * 100) / 100;
  if (alpha >= 1) {
    return `rgb(${r}, ${g}, ${b})`;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
function hexToHue(hex) {
  validateInput(HexToHueInputSchema, "hexToHue", hex);
  const [h] = hexToHsl(hex);
  return h;
}
function hexToHsl(hex) {
  validateInput(HexToHslInputSchema, "hexToHsl", hex);
  const [r, g, b] = hexToRgb(hex);
  const rf = r / 255;
  const gf = g / 255;
  const bf = b / 255;
  const max = Math.max(rf, gf, bf);
  const min = Math.min(rf, gf, bf);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, Math.round(l * 100)];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rf) h = ((gf - bf) / d + (gf < bf ? 6 : 0)) * 60;
  else if (max === gf) h = ((bf - rf) / d + 2) * 60;
  else h = ((rf - gf) / d + 4) * 60;
  return [Math.round(h), Math.round(s * 100), Math.round(l * 100)];
}
function hslToHex(h, s, l) {
  validateInput(HslToHexInputSchema, "hslToHex", [h, s, l]);
  const sNorm = s / 100;
  const lNorm = l / 100;
  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const hSection = h / 60;
  const x = c * (1 - Math.abs(hSection % 2 - 1));
  const m = lNorm - c / 2;
  let rf;
  let gf;
  let bf;
  if (hSection < 1) {
    rf = c;
    gf = x;
    bf = 0;
  } else if (hSection < 2) {
    rf = x;
    gf = c;
    bf = 0;
  } else if (hSection < 3) {
    rf = 0;
    gf = c;
    bf = x;
  } else if (hSection < 4) {
    rf = 0;
    gf = x;
    bf = c;
  } else if (hSection < 5) {
    rf = x;
    gf = 0;
    bf = c;
  } else {
    rf = c;
    gf = 0;
    bf = x;
  }
  const toHex = (v) => {
    const clamped = Math.max(0, Math.min(255, Math.round((v + m) * 255)));
    return clamped.toString(16).padStart(2, "0");
  };
  return `#${toHex(rf)}${toHex(gf)}${toHex(bf)}`;
}

// src/version.ts
var LIB_VERSION = "0.6.4";

// src/index.ts
registerLazy(
  "@norkron/web-color",
  () => selfCheck
);

export { LIB_VERSION, apcaContrast, contrastRatio, hexToHsl, hexToHue, hexToOklch, hexToRgb, hexToRgbString, hslToHex, selfCheck, srgbLuminance, wcagGrade };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map